import type { SideDrawer } from "../side-drawer/side-drawer";
import { setSearchModalActive } from "../../utils/search-modal-active";
import { setupSideMenuLocale } from "./side-menu-locale";

declare global {
  interface HTMLElementTagNameMap {
    "header-new": HeaderNew;
  }
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

const SIDE_MENU_ROOT_ID = "side-menu-root";

const NAV_TRACKING_EXCLUDED_SELECTORS = [
  ".header-new__logo a",
  "[data-side-menu-toggle]",
  "[data-modal-toggle='#search-modal-new']",
  "[data-side-menu-cart]",
  ".shopping-bag",
].join(",");

class HeaderNew extends HTMLElement {
  private drawer: SideDrawer | null = null;
  private toggleButton: HTMLElement | null;
  private handleKeyDown: (e: KeyboardEvent) => void;
  private handleBreakpointChange: () => void;
  private searchModal: HTMLElement | null;
  private breakpointQuery: MediaQueryList;
  private isHomePage: boolean;
  private scrollHandler: (() => void) | null;
  private homepageScrollHandler: (() => void) | null;

  constructor() {
    super();

    this.toggleButton = this.querySelector("[data-side-menu-toggle]");
    this.searchModal = document.querySelector("#search-modal-new");

    this.breakpointQuery = window.matchMedia("(min-width: 992px)");
    this.handleKeyDown = this.onKeyDown.bind(this);
    this.handleBreakpointChange = () => {
      if (this.isMenuOpen()) {
        this.closeMenu();
      }
    };

    this.isHomePage = this.hasAttribute("data-homepage");
    this.scrollHandler = null;
    this.homepageScrollHandler = null;
  }

  connectedCallback(): void {
    if (this.isHomePage) {
      this.homepageScrollHandler = this.onScroll.bind(this);
      window.addEventListener("scroll", this.homepageScrollHandler, {
        passive: true,
      });
      this.onScroll();
    }

    if (this.dataset.scrollHide) {
      this.setupScrollHide();
    }

    this.toggleButton?.addEventListener("click", (e) => {
      e.preventDefault();
      if (this.isMenuOpen()) {
        this.closeMenu();
      } else {
        this.closeSearch();
        this.openMenu();
      }
    });

    this.querySelectorAll<HTMLElement>(
      '[data-new-modal-toggle="#search-modal-new"]',
    ).forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        if (this.isMenuOpen()) this.closeMenu();
        if (this.searchModal?.classList.contains("reveal")) {
          e.preventDefault();
          e.stopPropagation();
          (
            window as unknown as {
              closeThemeModal?: (immediate?: boolean) => void;
            }
          ).closeThemeModal?.();
          setSearchModalActive(false);
        } else {
          this.closest(".header-wrapper")?.classList.remove("is-hidden");
          setSearchModalActive(true);
        }
      });
    });

    this.drawer = this.querySelector<SideDrawer>(`#${SIDE_MENU_ROOT_ID}`);
    if (!this.drawer) return;

    const rootOverlay = this.drawer.querySelector<HTMLElement>(
      ".side-drawer__overlay",
    );
    rootOverlay?.addEventListener(
      "click",
      (e) => {
        e.stopImmediatePropagation();
        this.closeMenu();
      },
      true,
    );

    // Close (X) buttons close the whole menu regardless of which drawer they sit in.
    this.querySelectorAll<HTMLElement>("[data-side-menu-close]").forEach(
      (btn) => {
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          this.closeMenu();
        });
      },
    );

    // Cart icon: close the menu on click so the cart route takes over.
    this.querySelectorAll<HTMLElement>("[data-side-menu-cart]").forEach(
      (cart) => {
        cart.addEventListener("click", () => this.closeMenu());
      },
    );

    // Breadcrumb row (or the back arrow inside it): close just the enclosing
    // drawer (return one level).
    this.querySelectorAll<HTMLElement>("[data-drawer-back]").forEach((back) => {
      back.addEventListener("click", (e) => {
        e.preventDefault();
        const drawer = back.closest<SideDrawer>("side-drawer");
        drawer?.close();
      });
    });

    // Triggers that open a nested drawer by id.
    this.querySelectorAll<HTMLElement>("[data-drawer-open]").forEach(
      (trigger) => {
        trigger.addEventListener("click", (e) => {
          e.preventDefault();
          const id = trigger.getAttribute("data-drawer-open");
          if (id) this.openDrawerById(id);
        });
      },
    );

    // Breadcrumb jump-to-level: close any drawers deeper than the targeted id.
    // stopPropagation so the outer breadcrumb's [data-drawer-back] doesn't also fire.
    this.querySelectorAll<HTMLElement>("[data-drawer-goto]").forEach((link) => {
      link.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.gotoDrawer(link.getAttribute("data-drawer-goto"));
      });
    });

    // When the root drawer is closed (e.g. its own overlay click), cascade-close
    // any submenu drawers that may still be active.
    this.drawer.addEventListener("side-drawer:close", (e) => {
      if (e.target !== this.drawer) return;
      this.closeSubmenus();
      this.clearMenuItemState();
      this.classList.remove("side-menu-open");
      document.body.removeAttribute("data-side-menu-active");
    });

    // Image-grid accordion toggles (inline, not a drawer).
    this.querySelectorAll<HTMLElement>("[data-menu-toggle]").forEach(
      (toggle) => {
        toggle.addEventListener("click", (e) => {
          e.preventDefault();
          this.toggleSubmenu(toggle);
        });
      },
    );

    setupSideMenuLocale(this);

    this.addEventListener("click", this.handleNavTrackingClick, {
      capture: true,
      passive: true,
    });

    document.addEventListener("keydown", this.handleKeyDown);
    this.breakpointQuery.addEventListener(
      "change",
      this.handleBreakpointChange,
    );
  }

  disconnectedCallback(): void {
    document.removeEventListener("keydown", this.handleKeyDown);
    this.breakpointQuery.removeEventListener(
      "change",
      this.handleBreakpointChange,
    );
    if (this.scrollHandler) {
      window.removeEventListener("scroll", this.scrollHandler);
    }
  }

  private handleNavTrackingClick = (event: Event): void => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const link = target.closest("a");
    if (!(link instanceof HTMLAnchorElement)) return;

    const href = link.getAttribute("href");
    if (!href || href === "#" || href.startsWith("javascript:")) return;

    if (
      link.matches(NAV_TRACKING_EXCLUDED_SELECTORS) ||
      link.closest(NAV_TRACKING_EXCLUDED_SELECTORS)
    ) {
      return;
    }

    const ariaLabel = link.getAttribute("aria-label")?.trim();
    const text = link.textContent?.replace(/\s+/g, " ").trim();
    const label = ariaLabel || text || link.pathname || link.href;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "navigation_click",
      nav_item_label: label,
      nav_item_url: link.href,
      nav_location: this.breakpointQuery.matches ? "desktop" : "mobile",
    });
  };

  private onScroll(): void {
    const scrollThreshold = 100;
    if (window.scrollY > scrollThreshold) {
      this.classList.add("header-new--scrolled");
    } else {
      this.classList.remove("header-new--scrolled");
    }
  }

  private onKeyDown(e: KeyboardEvent): void {
    if (e.key === "Escape" && this.isMenuOpen()) {
      this.closeMenu();
    }
  }

  private openDrawerById(id: string): void {
    const el = this.querySelector<SideDrawer>(`#${CSS.escape(id)}`);
    el?.open();
  }

  private closeSubmenus(): void {
    this.querySelectorAll<SideDrawer>("side-drawer.active").forEach((d) => {
      if (d !== this.drawer) d.close();
    });
  }

  private gotoDrawer(target: string | null): void {
    if (!target) return;
    const descendantPrefix = `${target}-`;
    this.querySelectorAll<SideDrawer>("side-drawer.active").forEach((d) => {
      if (d === this.drawer) return;
      if (d.id.startsWith(descendantPrefix)) d.close();
    });
    const targetEl = this.querySelector<SideDrawer>(`#${CSS.escape(target)}`);
    targetEl?.open();
  }

  private clearMenuItemState(): void {
    this.querySelectorAll("[data-menu-item].active").forEach((item) => {
      item.classList.remove("active");
    });
  }

  private toggleSubmenu(toggle: HTMLElement): void {
    const menuItem = toggle.closest("[data-menu-item]");
    if (!menuItem) return;

    const isActive = menuItem.classList.contains("active");

    this.querySelectorAll("[data-menu-item].active").forEach((item) => {
      if (item !== menuItem) {
        item.classList.remove("active");
      }
    });

    if (isActive) {
      menuItem.classList.remove("active");
    } else {
      menuItem.classList.add("active");
    }
  }

  private openMenu(): void {
    this.classList.add("side-menu-open");
    document.body.setAttribute("data-side-menu-active", "");
    this.drawer?.open();
  }

  private closeMenu(): void {
    const subs = Array.from(
      this.querySelectorAll<SideDrawer>("side-drawer.active"),
    ).filter((d) => d !== this.drawer);

    const finishClose = (): void => {
      this.drawer?.close();
      this.clearMenuItemState();
      this.classList.remove("side-menu-open");
      document.body.removeAttribute("data-side-menu-active");
    };

    if (subs.length === 0) {
      finishClose();
      return;
    }
    const deepest = subs[subs.length - 1];
    const panel = deepest.querySelector<HTMLElement>(".side-drawer__panel");
    const onDone = (e: TransitionEvent): void => {
      if (e.propertyName !== "transform" || e.target !== panel) return;
      panel?.removeEventListener("transitionend", onDone);
      finishClose();
    };
    panel?.addEventListener("transitionend", onDone);

    subs.forEach((d) => d.close());
  }

  private closeSearch(): void {
    if (!this.searchModal?.classList.contains("reveal")) return;
    this.searchModal.classList.remove("reveal");
    setSearchModalActive(false);
  }

  private isMenuOpen(): boolean {
    return this.drawer?.isOpen || false;
  }

  private setupScrollHide(): void {
    const headerWrapper = this.closest(".header-wrapper");
    if (!headerWrapper) return;

    headerWrapper.classList.add("is-fixed");

    let lastScrollY = window.scrollY;
    let ticking = false;

    this.scrollHandler = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          if (document.body.classList.contains("modal-active")) {
            lastScrollY = currentScrollY;
            ticking = false;
            return;
          }
          if (
            currentScrollY > lastScrollY &&
            currentScrollY > headerWrapper.clientHeight
          ) {
            headerWrapper.classList.add("is-hidden");
          } else if (currentScrollY < lastScrollY) {
            headerWrapper.classList.remove("is-hidden");
          }
          lastScrollY = currentScrollY;
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", this.scrollHandler, { passive: true });
  }
}

if (!window.customElements.get("header-new")) {
  window.customElements.define("header-new", HeaderNew);
}

export {};
